import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/auth';
import { apiFetch, ApiError } from './lib/api';
import { reconcileDevice } from './lib/onesignal';
import { SignIn } from './pages/SignIn';
import { Register } from './pages/Register';
import { MarketingLanding } from './pages/MarketingLanding';
import { Paywall } from './pages/Paywall';
import { Sidebar } from './components/Sidebar';
import { Topbar } from './components/Topbar';
import { BottomNav } from './components/BottomNav';
import { WaPairModal } from './components/WaPairModal';
import { ChatFAB } from './components/ChatFAB';
import { OnboardingChecklist } from './components/OnboardingChecklist';
import { UpdateBanner } from './components/UpdateBanner';
import ImpersonationBanner from './components/ImpersonationBanner';
import ImpersonatePage from './pages/Impersonate';
import AuthContinue from './pages/AuthContinue';
import LegalPage from './pages/LegalPage';
import { Dashboard } from './pages/Dashboard';
import { Clients } from './pages/Clients';
import { ClientDetail } from './pages/ClientDetail';
import { Contacts } from './pages/Contacts';
import { AddClient } from './pages/AddClient';
import { AddCase } from './pages/AddCase';
import { NewCase } from './pages/NewCase';
import { Cases } from './pages/Cases';
import { CaseDetail } from './pages/CaseDetail';
import { Leads } from './pages/Leads';
import { LeadDetail } from './pages/LeadDetail';
import { Documents } from './pages/Documents';
import { Calendar } from './pages/Calendar';
import { Tasks } from './pages/Tasks';
import { TimePage } from './pages/TimePage';
import { Billing } from './pages/Billing';
import { DevAuth } from './pages/DevAuth';
import { Settings } from './pages/Settings';
import type { PageKey, FirmSummary } from './types';

type Membership = { firm: FirmSummary; role: string; joinedAt: string };
type Me = {
  user: {
    id: string;
    email: string;
    name: string | null;
    firstName?: string | null;
    lastName?: string | null;
    phone?: string | null;
    profileCompletedAt?: string | null;
    defaultLocale?: string;
    theme?: string;
    sidebarCollapsed?: boolean;
  };
  memberships: Membership[];
  onboarding?: { complete: boolean; missing: string[] };
};

/**
 * Map the current URL pathname to a PageKey for sidebar/topbar highlighting.
 * Sub-routes (e.g. /clients/:id, /clients/new) collapse to their root key.
 */
function pageFromPath(pathname: string): PageKey {
  if (pathname.startsWith('/clients/new') || pathname === '/addclient') return 'addclient';
  if (pathname.startsWith('/clients')) return 'clients';
  if (pathname.startsWith('/contacts')) return 'contacts';
  if (pathname === '/cases/new') return 'newcase';
  if (pathname.startsWith('/cases')) return 'cases';
  if (pathname.startsWith('/documents')) return 'documents';
  if (pathname.startsWith('/calendar')) return 'calendar';
  if (pathname.startsWith('/tasks')) return 'tasks';
  if (pathname.startsWith('/time')) return 'time';
  if (pathname.startsWith('/billing')) return 'billing';
  if (pathname.startsWith('/settings')) return 'settings';
  if (pathname.startsWith('/leads')) return 'leads';
  return 'dashboard';
}

/** Reverse map: a PageKey from a sidebar/bottom-nav click → the URL path. */
function pathFromPage(key: PageKey): string {
  switch (key) {
    case 'dashboard':
      return '/dashboard';
    case 'addclient':
      return '/clients/new';
    case 'newcase':
      return '/cases/new';
    case 'clients':
      return '/clients';
    case 'contacts':
      return '/contacts';
    case 'cases':
      return '/cases';
    case 'documents':
      return '/documents';
    case 'calendar':
      return '/calendar';
    case 'tasks':
      return '/tasks';
    case 'time':
      return '/time';
    case 'billing':
      return '/billing';
    case 'leads':
      return '/leads';
    case 'settings':
      return '/settings';
  }
}

function AppShell({ me, refreshMe }: { me: Me; refreshMe: () => void }) {
  const { signOut } = useAuth();
  const { t, i18n } = useTranslation(['nav', 'google']);
  const location = useLocation();
  const navigate = useNavigate();

  const firm = me.memberships[0]!.firm;
  const role = me.memberships[0]!.role;
  const [searchQuery, setSearchQuery] = useState('');
  // Single source of truth for the WhatsApp pair modal so both the
  // sidebar UserMenu and the onboarding checklist trigger the same flow.
  const [waPairOpen, setWaPairOpen] = useState(false);
  // Mobile sidebar drawer — opened by BottomNav "More", closed by
  // backdrop click or any nav click.
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  // Desktop sidebar collapse — persisted on User.sidebarCollapsed.
  // Local state is the source of truth for instant toggle; we PATCH /api/me
  // in the background to persist.
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(
    Boolean(me.user.sidebarCollapsed),
  );

  // Sync the server-side theme/locale/sidebar prefs into the SPA on
  // login. Track the last-applied values so we ONLY re-sync when the
  // server value changes — without that guard, the effect re-runs every
  // time i18n.language flips locally (which it does on every
  // LanguageSwitcher click), and naively applying the server value
  // would fight the user.
  const syncedFromMe = useRef<{ theme?: string; locale?: string }>({});
  useEffect(() => {
    if (
      me.user.theme &&
      me.user.theme !== syncedFromMe.current.theme &&
      (me.user.theme === 'system' || me.user.theme === 'light' || me.user.theme === 'dark')
    ) {
      syncedFromMe.current.theme = me.user.theme;
      void import('./lib/theme').then(({ applyTheme }) =>
        applyTheme(me.user.theme as 'system' | 'light' | 'dark'),
      );
    }
    if (
      me.user.defaultLocale &&
      me.user.defaultLocale !== syncedFromMe.current.locale &&
      (me.user.defaultLocale === 'he' || me.user.defaultLocale === 'en')
    ) {
      syncedFromMe.current.locale = me.user.defaultLocale;
      if (i18n.language !== me.user.defaultLocale) {
        void i18n.changeLanguage(me.user.defaultLocale);
      }
    }
    if (typeof me.user.sidebarCollapsed === 'boolean') {
      setSidebarCollapsed(me.user.sidebarCollapsed);
    }
    // i18n is intentionally NOT in the dep list — re-running on i18n
    // change would cause the loop described above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me.user.theme, me.user.defaultLocale, me.user.sidebarCollapsed]);

  // Mirror sidebar-collapsed onto <html> so .main can pick it up via CSS.
  useEffect(() => {
    if (sidebarCollapsed) {
      document.documentElement.setAttribute('data-sidebar-collapsed', 'true');
    } else {
      document.documentElement.removeAttribute('data-sidebar-collapsed');
    }
  }, [sidebarCollapsed]);

  // OneSignal SDK is NOT auto-loaded here. It's lazy-loaded inside the
  // Settings "Enable notifications" click handler so the SDK CDN +
  // service-worker registration only happen when the user has
  // explicitly opted in. The SW stays registered after that and
  // delivers pushes whether or not the SDK is loaded on subsequent
  // page loads.
  //
  // However, if the browser already has push permission, silently
  // reconcile on mount to keep deviceType (desktop vs mobile) current.
  // This ensures a device registered before the mobile-detection
  // feature gets reclassified the next time the user opens the app.
  useEffect(() => {
    if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      void reconcileDevice();
    }
    // Run once per app mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // After Google OAuth redirect, show toast + register calendar push.
  // Lives at app-shell level because the redirect may land on any
  // route, not just /settings.
  useEffect(() => {
    const url = new URL(window.location.href);
    const result = url.searchParams.get('google');
    if (result === 'connected') {
      url.searchParams.delete('google');
      url.searchParams.delete('reason');
      window.history.replaceState({}, '', url.toString());
      apiFetch('/api/calendar/watch', { method: 'POST' }).catch(() => {});
      alert(t('google:errors.connected'));
    } else if (result === 'error') {
      const reason = url.searchParams.get('reason') ?? 'unknown';
      url.searchParams.delete('google');
      url.searchParams.delete('reason');
      window.history.replaceState({}, '', url.toString());
      const key = `google:errors.${reason}`;
      const translated = t(key);
      const message = translated === key ? t('google:errors.generic', { reason }) : translated;
      alert(message);
    }
    // Run only on first mount of the AppShell.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      // Best-effort persist; failure doesn't roll back the local toggle —
      // user just sees their preference reset on next login if /api/me
      // PATCH was offline.
      void apiFetch('/api/me', {
        method: 'PATCH',
        body: JSON.stringify({ sidebarCollapsed: next }),
      }).catch(() => {});
      return next;
    });
  }, []);

  const sidebarPage = pageFromPath(location.pathname);

  const goPage = useCallback(
    (p: PageKey) => {
      navigate(pathFromPage(p));
      // Reset search when navigating away from search-aware pages
      setSearchQuery('');
    },
    [navigate],
  );

  // Reset search query whenever the active page (root-level) changes
  useEffect(() => {
    setSearchQuery('');
  }, [sidebarPage]);

  const userName = me.user.name?.trim() || me.user.email.split('@')[0]!;
  const roleKey = `roles.${role}`;
  const userRole = i18n.exists(roleKey, { ns: 'nav' }) ? t(roleKey) : role;

  return (
    <div className="app">
      <Sidebar
        currentPage={sidebarPage}
        onNavigate={goPage}
        firmName={firm.name}
        userName={userName}
        userRole={userRole}
        onSignOut={signOut}
        mobileOpen={mobileSidebarOpen}
        onCloseMobile={() => setMobileSidebarOpen(false)}
        collapsed={sidebarCollapsed}
        onToggleCollapse={toggleSidebar}
      />
      <div className="main">
        <Topbar
          currentPage={sidebarPage}
          onNavigate={goPage}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
        />
        <main className="content">
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route
              path="/dashboard"
              element={
                <>
                  <OnboardingChecklist
                    onNavigate={goPage}
                    onConnectWa={() => setWaPairOpen(true)}
                    onConnectGoogle={async () => {
                      try {
                        const { url } = await apiFetch<{ url: string }>('/api/auth/google/start', { method: 'POST' });
                        window.location.href = url;
                      } catch {
                        // The UserMenu also surfaces connect errors.
                      }
                    }}
                  />
                  <Dashboard firmName={firm.name} />
                </>
              }
            />
            <Route
              path="/clients"
              element={
                <Clients
                  onAddClient={() => navigate('/clients/new')}
                  onOpen={(clientId) => navigate(`/clients/${clientId}`)}
                  searchQuery={searchQuery}
                />
              }
            />
            <Route
              path="/clients/new"
              element={
                <AddClient
                  onSaved={() => {
                    refreshMe();
                    navigate('/clients');
                  }}
                  onCancel={() => navigate('/clients')}
                />
              }
            />
            <Route
              path="/clients/:id"
              element={<ClientDetailRoute firmId={firm.id} onChanged={refreshMe} />}
            />
            <Route
              path="/clients/:id/cases/new"
              element={<AddCaseRoute />}
            />
            <Route
              path="/contacts"
              element={
                <Contacts onPromoted={(clientId) => navigate(`/clients/${clientId}`)} />
              }
            />
            <Route
              path="/cases"
              element={
                <Cases
                  onOpen={(caseId) => navigate(`/cases/${caseId}`)}
                  searchQuery={searchQuery}
                />
              }
            />
            <Route
              path="/cases/new"
              element={
                <NewCase
                  onSaved={(caseId) => navigate(`/cases/${caseId}`)}
                  onCancel={() => navigate('/cases')}
                />
              }
            />
            <Route path="/cases/:id" element={<CaseDetail firmId={firm.id} />} />
            <Route
              path="/documents"
              element={<Documents firmId={firm.id} searchQuery={searchQuery} />}
            />
            <Route path="/calendar" element={<Calendar />} />
            <Route path="/tasks" element={<Tasks searchQuery={searchQuery} />} />
            <Route path="/time" element={<TimePage />} />
            <Route path="/billing" element={<Billing searchQuery={searchQuery} />} />
            <Route path="/leads" element={<Leads />} />
            <Route path="/leads/:id" element={<LeadDetail />} />
            <Route
              path="/settings"
              element={
                <Settings
                  firmId={firm.id}
                  onConnectWa={() => setWaPairOpen(true)}
                />
              }
            />

            {/* Legacy paths from the pre-router state-based view; redirect. */}
            <Route path="/addclient" element={<Navigate to="/clients/new" replace />} />

            {/* Anything unmapped → dashboard */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </main>
      </div>
      <BottomNav
        currentPage={sidebarPage}
        onNavigate={goPage}
        onOpenMore={() => setMobileSidebarOpen(true)}
      />
      {waPairOpen && (
        <WaPairModal
          firmId={firm.id}
          onPaired={() => {
            setWaPairOpen(false);
            refreshMe();
          }}
          onClose={() => setWaPairOpen(false)}
        />
      )}
      <ChatFAB />
    </div>
  );
}

/**
 * URL-driven wrapper for ClientDetail. Pulls the :id from the route and
 * translates the existing callback API into navigate() calls so we don't
 * have to rewrite ClientDetail itself.
 */
function ClientDetailRoute({ firmId, onChanged }: { firmId: string; onChanged: () => void }) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  if (!id) return <Navigate to="/clients" replace />;
  return (
    <ClientDetail
      key={id}
      clientId={id}
      firmId={firmId}
      onBack={() => navigate('/clients')}
      onAddCase={(clientId, clientName) => {
        // Pass the name via location state so AddCase can show it without
        // re-fetching the client.
        navigate(`/clients/${clientId}/cases/new`, { state: { clientName } });
      }}
      onDeleted={() => {
        onChanged();
        navigate('/clients');
      }}
    />
  );
}

function AddCaseRoute() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const clientName = (location.state as { clientName?: string } | null)?.clientName ?? '';
  if (!id) return <Navigate to="/clients" replace />;
  return (
    <AddCase
      clientId={id}
      clientName={clientName}
      onSaved={() => navigate(`/clients/${id}`)}
      onCancel={() => navigate(`/clients/${id}`)}
    />
  );
}

function PostAuthGate() {
  const { signOut } = useAuth();
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiFetch<Me>('/api/me');
      setMe(data);
    } catch (err) {
      if (err instanceof ApiError && err.status === 503) {
        await signOut();
      }
      setMe(null);
    } finally {
      setLoading(false);
    }
  }, [signOut]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (loading) return <main className="signin-wrap"><p>…</p></main>;
  if (!me) return <ProfileError />;

  // Onboarding gate. The shared @lexcrm/shared service decides — if any
  // required field is missing (firstName, phone, or "no firm yet"), we
  // bump the user to /register where the wizard resumes them at the
  // right step. Adding a new required field there auto-cascades here.
  if (me.onboarding && !me.onboarding.complete) {
    if (typeof window !== 'undefined' && window.location.pathname !== '/register') {
      window.location.replace('/register');
      return <main className="signin-wrap"><p>…</p></main>;
    }
    return null;
  }

  // Hard paywall — backend 402s tenant routes if the firm is past_due
  // / canceled / trial-expired. Mirror the same logic here so we render
  // a paywall page instead of a half-broken dashboard. Server is
  // authoritative: even if this client check is wrong, the api blocks.
  const firm = me.memberships[0]!.firm;
  const reason = paywallReason(firm);
  if (reason) {
    return (
      <Paywall
        reason={reason}
        trialEndsAt={firm.trialEndsAt ?? null}
        onRenewed={refresh}
      />
    );
  }

  return <AppShell me={me} refreshMe={refresh} />;
}

/**
 * Mirrors backend lib/subscription-status.ts checkSubscriptionAccess.
 * Returns null when access is allowed, or a reason string otherwise.
 */
function paywallReason(firm: FirmSummary): string | null {
  const status = firm.subscriptionStatus;
  if (status === 'active') return null;
  if (status === 'trialing') {
    // Mirror server-side checkSubscriptionAccess: a 'trialing' firm
    // without a stripeSubscriptionId hasn't actually set up payment
    // (POST /api/firms auto-stamps the status before any Stripe work
    // happens). Force them through the paywall, which lands on the
    // same Stripe Elements flow as the renewal page.
    if (!firm.stripeSubscriptionId) return 'no_subscription';
    const ends = firm.trialEndsAt ? new Date(firm.trialEndsAt).getTime() : 0;
    if (ends > Date.now()) return null;
    return 'trial_expired';
  }
  if (status === 'past_due') return 'past_due';
  if (status === 'canceled') return 'canceled';
  if (status === 'unpaid') return 'unpaid';
  if (status === 'incomplete_expired') return 'incomplete_expired';
  if (!status) return null; // pre-v3, treat as grandfathered until backend disagrees
  return null;
}

function ProfileError() {
  const { t } = useTranslation();
  return (
    <main className="signin-wrap">
      <p>{t('errors.loading_profile')}</p>
    </main>
  );
}

/**
 * Authenticated root. Unauth users see the marketing landing at /, and
 * /signin / /register render outside the gate (see Root() below).
 */
function Gate() {
  const { user, loading } = useAuth();
  if (loading) return <main className="signin-wrap"><p>…</p></main>;
  if (!user) return <MarketingLanding />;
  return <PostAuthGate />;
}

/**
 * Top-level routing wrapper. Public routes (/register, /signin) render
 * outside the auth gate so unauth users can reach them; /dev-auth is a
 * special route that consumes an audit token and signs in via Firebase
 * custom token, also reachable without an existing session.
 */
function Root() {
  if (typeof window !== 'undefined' && window.location.pathname === '/dev-auth') {
    return <DevAuth />;
  }
  return (
    <Routes>
      <Route path="/register" element={<Register />} />
      <Route path="/signin" element={<SignInRoute />} />
      {/* Deep-link entry point for admin impersonation. Handles
          `?token=<customToken>` (signs in via signInWithCustomToken)
          and bounces to /. The resulting Firebase session carries the
          `imp_by` claim, which ImpersonationBanner picks up. */}
      <Route path="/auth/impersonate" element={<ImpersonatePage />} />
      {/* Magic-link from the email-code email: `#e=…&c=…` in the fragment.
          Verifies the code, signs in, and resumes the registration wizard. */}
      <Route path="/auth/continue" element={<AuthContinue />} />
      {/* Public legal pages — also used as the OAuth consent screen's
          privacy-policy / terms links. No auth required. */}
      <Route path="/terms" element={<LegalPage doc="terms" />} />
      <Route path="/privacy" element={<LegalPage doc="privacy" />} />
      <Route path="*" element={<Gate />} />
    </Routes>
  );
}

function SignInRoute() {
  const { user } = useAuth();
  if (user) return <Navigate to="/" replace />;
  return <SignIn />;
}

export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Root />
        {/* Outside Gate so the banner shows on the sign-in page too. */}
        <UpdateBanner />
        {/* Persistent red banner whenever the current Firebase session
            carries an imp_by claim (admin is signed in *as* this user
            via /api/admin/impersonate/.../start). */}
        <ImpersonationBanner />
      </AuthProvider>
    </BrowserRouter>
  );
}
